import React from 'react';
import Svg, { Circle, Path, Rect } from 'react-native-svg';

/**
 * Cohesive football-themed icon set, recreated from the Volt design handoff.
 * Every glyph is normalised to a 24×24 viewBox with a 1.7 stroke and round
 * caps/joins, so the whole set carries one optical weight at any size - the
 * tab bar, the More menu and every settings row speak the same visual language
 * instead of mixing stock icons.
 */
export interface IconProps {
  size?: number;
  color: string;
}

const SW = 1.7;

/** Matches - a classic football (panelled ball). */
export function FootballIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={8.5} stroke={color} strokeWidth={SW} />
      <Path
        d="M12 8.9l2.95 2.14-1.13 3.47h-3.64L8.05 11.04 12 8.9z"
        fill={color}
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path
        d="M12 8.9V3.5M14.95 11.04l5.13-1.67M13.82 14.51L17 18.88M10.18 14.51L7 18.88M9.05 11.04L3.92 9.37"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Widgets - a 2x2 grid of rounded tiles. */
export function WidgetIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={4} y={4} width={7} height={7} rx={2} stroke={color} strokeWidth={SW} />
      <Rect x={13} y={4} width={7} height={7} rx={2} stroke={color} strokeWidth={SW} />
      <Rect x={4} y={13} width={7} height={7} rx={2} stroke={color} strokeWidth={SW} />
      <Rect x={13} y={13} width={7} height={7} rx={2} stroke={color} strokeWidth={SW} />
    </Svg>
  );
}

/** Leagues - a trophy with handles. */
export function TrophyIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M7 5h10v3a5 5 0 01-10 0V5z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path
        d="M7 6C4.6 6 4.6 9.8 7.4 10.2M17 6c2.4 0 2.4 3.8-.4 4.2"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
      />
      <Path
        d="M12 13v3M9 19h6M10 16h4"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Following - a star. */
export function StarIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 4l1.9 5.4 5.7.1-4.6 3.5 1.7 5.5L12 15.2l-4.7 3.3 1.7-5.5L4.4 9.5l5.7-.1L12 4z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Fantasy - a football shirt. */
export function JerseyIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M8.5 4.5L6 5.5 3.8 8 6 10.2 8 8.6v9.8c0 .7.3 1 1 1h6c.7 0 1-.3 1-1V8.6l2 1.6L18.2 8 16 5.5l-2.5-1"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <Path d="M8.5 4.5C10 7 14 7 15.5 4.5" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** News - a newspaper. */
export function NewsIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M6 5h10a2 2 0 012 2v11a1 1 0 01-1 1H6a2 2 0 01-2-2V7a2 2 0 012-2z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M7.5 9h7M7.5 12.5h7M7.5 16h4" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** Predictions - a probability bar chart. */
export function PredictIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4.5 20h15" stroke={color} strokeWidth={SW} strokeLinecap="round" />
      <Path
        d="M7 20v-5M12 20V8M17 20v-7"
        stroke={color}
        strokeWidth={SW + 0.4}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Lineup builder - a pitch / formation. */
export function PitchIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M4 12h16" stroke={color} strokeWidth={SW} />
      <Circle cx={12} cy={12} r={2.6} stroke={color} strokeWidth={SW} />
      <Path d="M9.5 4v2.5h5V4M9.5 20v-2.5h5V20" stroke={color} strokeWidth={SW} strokeLinejoin="round" />
    </Svg>
  );
}

/** Profile - a person. */
export function PersonIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={8.5} r={3.4} stroke={color} strokeWidth={SW} />
      <Path d="M5.5 20a6.5 6.5 0 0113 0" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** Transfer centre - swap arrows. */
export function TransferIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M5 9l3.5-3.5M5 9l3.5 3.5M5 9h13M19 15l-3.5 3.5M19 15l-3.5-3.5M19 15H6"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Notifications - a bell. */
export function BellIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 3a6 6 0 00-6 6v4l-2 3h16l-2-3V9a6 6 0 00-6-6z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M10 19a2 2 0 004 0" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** Settings - a gear. */
export function GearIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={3} stroke={color} strokeWidth={SW} />
      <Path
        d="M12 3.5v2.2M12 18.3v2.2M4.5 12H6.7M17.3 12h2.2M6.1 6.1l1.6 1.6M16.3 16.3l1.6 1.6M17.9 6.1l-1.6 1.6M7.7 16.3l-1.6 1.6"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** TV schedules - a television. */
export function TvIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M5 7h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M9 3.5L12 7l3-3.5M8.5 21h7" stroke={color} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** More - a hamburger menu. */
export function MenuIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M5 8h14M5 12h14M5 16h14" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** Search - a magnifier. */
export function SearchIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={10.5} cy={10.5} r={6} stroke={color} strokeWidth={SW} />
      <Path d="M15 15l5 5" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** Language - a globe with meridians. */
export function GlobeIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={8} stroke={color} strokeWidth={SW} />
      <Path
        d="M4 12h16M12 4c2.4 2.2 2.4 13.8 0 16M12 4c-2.4 2.2-2.4 13.8 0 16"
        stroke={color}
        strokeWidth={SW}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** How to play - an open guide / book. */
export function BookIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 6c-1.6-1.2-3.6-1.6-6-1.5v12c2.4-.1 4.4.3 6 1.5 1.6-1.2 3.6-1.6 6-1.5v-12c-2.4-.1-4.4.3-6 1.5z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M12 6v12" stroke={color} strokeWidth={SW} strokeLinecap="round" />
    </Svg>
  );
}

/** About - an info badge. */
export function InfoIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={8} stroke={color} strokeWidth={SW} />
      <Path d="M12 11v5" stroke={color} strokeWidth={SW + 0.1} strokeLinecap="round" />
      <Circle cx={12} cy={8} r={1} fill={color} />
    </Svg>
  );
}

/** Privacy - a shield with a check. */
export function ShieldIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 3l7 2.5v5.5c0 4.2-2.9 7.4-7 9-4.1-1.6-7-4.8-7-9V5.5L12 3z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M9 12l2.2 2.2L15 10" stroke={color} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Kickoff / deadline - a clock. */
export function ClockIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={8} stroke={color} strokeWidth={SW} />
      <Path d="M12 8v4.2l2.8 1.8" stroke={color} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Full-time - a referee whistle. */
export function WhistleIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M10 9h9a2 2 0 012 2 6 6 0 11-11.7-1.9L10 9z"
        stroke={color}
        strokeWidth={SW}
        strokeLinejoin="round"
      />
      <Path d="M10 6v3" stroke={color} strokeWidth={SW} strokeLinecap="round" />
      <Circle cx={15} cy={13.5} r={1} fill={color} />
    </Svg>
  );
}

/** Row affordance - a forward chevron (mirror with scaleX for RTL at call site). */
export function ChevronIcon({ size = 22, color }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M9 6l6 6-6 6" stroke={color} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}
